import { DatePicker, Dropdown, NumberField, Section, TextField } from 'm2m.components';
import { Button } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import React, { useEffect, useState, useCallback } from 'react';
import { Grid, makeStyles, Typography } from '@material-ui/core';
import moment from 'moment';
import { connect, useDispatch, useSelector } from 'react-redux';
import 'assets/style.css';

import {
  FuncaoParticipante,
  ParticipanteResponse,
  BancoResponse,
  GrupoAcesso,
  InstrumentoFinanceiro,
  TipoRegistroInstrumentoFinanceiro,
  AnbimaFundoResponse,
} from 'common/genericTypes';
import { UploadDocumentType, DocumentInfo } from 'common/genericTypes';
import { documentShortDescriptions, documentLongDescriptions } from 'common/genericDescriptions';
import { Option } from 'common/genericOptions';
import UploadDocs, { Attachment } from 'components/uploadDocs';
import { updateDocuments, updateDocumentsToDelete } from 'store/modules/cotasDeFundo/uploadDocumentos';

import { RootState } from 'store/index';
import { updateField, resetCotasDeFundoDetails } from 'store/modules/cotasDeFundo/caracteristicas';
import { CpfCnpjMask } from 'components/masks';
import ParticipanteAutoComplete from 'components/participante/participanteAutoComplete';
import { labelsCaracteristicas as labels } from '../labels';
import { updateId } from 'store/modules/cotasDeFundo/emissor';
import { addAlert } from 'store/modules/snackBar';
import { validaCpfCnpj } from 'utils/validation';
import { sanitizeAlphanumericCnpj } from 'utils/cnpjUtils';
import { useValidarCampos } from '../useValidarCampos';

const REACT_APP_SHOW_ANBIMA_BUTTON = process.env.REACT_APP_SHOW_ANBIMA_BUTTON === 'true';

const useStyles = makeStyles(() => ({
  formSection: {
    marginBottom: '24px',
  },
  fieldContainer: {
    marginBottom: '16px',
  },
  decimalInput: {
    width: '100%',
    padding: '10px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '14px',
  },
  flexContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  percentSymbol: {
    marginLeft: '8px',
  },
  cnpjContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  searchButton: {
    marginTop: '8px',
    height: '36px',
    width: 'fit-content',
  },
}));

const mapStateToProps = (state: RootState) => ({
  caracteristicas: state.cotasDeFundoCadastro.caracteristicas,
  highlightErrors: state.cotasDeFundoCadastro.highlightErrors,
  metadata: state.cotasDeFundoCadastro.metadata,
  participanteApi: state.participanteApi,
  usuarioApi: state.usuarioApi,
  isUsuarioOperacoes: state.auth.usuario.grupoAcesso === GrupoAcesso.Operacoes,
  isUsuarioCadastroOperacoes: state.auth.usuario.grupoAcesso === GrupoAcesso.CadastroOperacoes,
  isUsuarioCredenciado: state.auth.usuario.participante.funcao === FuncaoParticipante.Credenciado,
  isUsuarioCustodiante: state.auth.usuario.participante.funcao === FuncaoParticipante.Custodiante,
  isUsuarioEmissor: state.auth.usuario.participante.funcao === FuncaoParticipante.Emissor,
  participanteId: state.auth.usuario.participante.id,
  typeOfRegister: state.cotasDeFundoCadastro.typeOfRegister,
  instrumentoApi: state.instrumentoApi,
  uploadDocumentos: state.cotasDeFundoCadastro.uploadDocumentos,
});

const mapDispatchToProps = { updateField, updateId, updateDocuments, updateDocumentsToDelete };

type Props = ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps & { 
  participantes: ParticipanteResponse[], 
  codigo?: string, 
  delimitacaoEfetuada?: boolean,
  onRef?: (ref: { clearLocalFields: () => void }) => void
};

const customSelectOptions = [
  { value: 'ETF', description: 'ETF' },
  { value: 'FIDC', description: 'FIDC' },
  { value: 'FII', description: 'FII' },
  { value: 'FIP', description: 'FIP' },
  { value: 'FIF', description: 'FIF' },
  { value: 'OFFSHORE', description: 'OFFSHORE' },
  { value: 'INEXISTENTE', description: 'INEXISTENTE' },
  { value: 'OUTROS', description: 'OUTROS' }
];

const Caracteristicas: React.FC<Props> = (props) => {
  const {
    caracteristicas,
    highlightErrors,
    participanteApi: { getParticipantesSolicitadosPeloUsuario },
    usuarioApi: { getUsuarioMaster },
    updateField,
    updateId,
    metadata: {
      options,
      tiposFundo,
    },
    participanteId,
    participantes,
    typeOfRegister,
    onRef,
  } = props;

  const classes = useStyles();
  const [escrituradores, setEscrituradores] = useState<Array<ParticipanteResponse>>([]);
  const [custodiantes, setCustodiantes] = useState<Array<ParticipanteResponse>>([]);
  const [administradoresFiduciarios, setAdministradoresFiduciarios] = useState<Array<ParticipanteResponse>>([]);
  const [gestor, setGestor] = useState<string>('');
  const [anbimaFundoData, setAnbimaFundoData] = useState<AnbimaFundoResponse | null>(null);
  
  const { instrumentoApi } = props;
  const [escriturador, setEscriturador] = useState<ParticipanteResponse | null>(null);
  const [custodiante, setCustodiante] = useState<ParticipanteResponse | null>(null);
  const [administradorFiduciario, setAdministradorFiduciario] = useState<ParticipanteResponse | null>(null);
  const dispatch = useDispatch();
  const isUsuarioEmissor = useSelector((state: RootState) => state.auth.usuario.participante.funcao === FuncaoParticipante.Emissor);
  const isUsuarioAdministradorFiduciario = useSelector((state: RootState) => state.auth.usuario.participante.funcao === FuncaoParticipante.AdministradorFiduciario);
  const usuarioLogado = useSelector((state: RootState) => state.auth.usuario);
  const getNumeroEmissoesApi = useSelector((state: RootState) => state.instrumentoApi.getNumeroEmissoes);
  const camposInvalidos = useValidarCampos();
  const { uploadDocumentos: { documents, documentsToDelete } } = useSelector((state: RootState) => state.cotasDeFundoCadastro);
  const [uploadDocumentType, setUploadDocumentType] = useState<string>(UploadDocumentType.RegulamentoFundo);
  const documentOptions: Option[] = [
    { value: UploadDocumentType.RegulamentoFundo, description: documentShortDescriptions[UploadDocumentType.RegulamentoFundo] }
  ];

  const showError = (fieldLabel: string) => {
    return highlightErrors && camposInvalidos.includes(fieldLabel);
  };
  const showDocumentosError = showError(labels.documentos);

  const setParticipantes = useCallback(async () => {
    try {
      setEscrituradores(participantes.filter((p) => p.funcoes.some(f => f.funcao === FuncaoParticipante.Escriturador)));
      setCustodiantes(participantes.filter((p) => p.funcoes.some(f => f.funcao === FuncaoParticipante.Custodiante)));
      setAdministradoresFiduciarios(participantes.filter((p) => p.funcoes.some(f => f.funcao === FuncaoParticipante.AdministradorFiduciario)));
    } catch { }
  }, [participantes]);

  const handleGestorChange = (value: string | ParticipanteResponse) => {
    const razaoSocial = typeof value === 'string' ? value : value.razaoSocial;
    setGestor(razaoSocial);
    updateField({ name: 'gestor', value: razaoSocial });
  };

  const clearLocalFields = useCallback(() => {
    setGestor('');
    setEscriturador(null);
    setCustodiante(null);
    setAdministradorFiduciario(null);
    setAnbimaFundoData(null);
  }, []);

  useEffect(() => {
    if (onRef) {
      onRef({ clearLocalFields });
    }
  }, [onRef, clearLocalFields]);

  useEffect(() => {
    if (isUsuarioAdministradorFiduciario && participantes.length > 0) {
      const participanteUsuarioLogado = participantes.find(p => p.id === usuarioLogado.participante.id);
      if (participanteUsuarioLogado) {
        setAdministradorFiduciario(participanteUsuarioLogado);
        updateField({ name: 'administradorFiduciarioId', value: participanteUsuarioLogado.id });
        updateField({ name: 'administradorFiduciario', value: participanteUsuarioLogado.razaoSocial });
      }
    }
  }, [isUsuarioAdministradorFiduciario, participantes, usuarioLogado.participante.id, updateField]);

  const handleEscrituradorChange = useCallback((value: ParticipanteResponse | string | null) => {
    if (typeof value === 'string') {
      const participante = escrituradores.find(p => p.razaoSocial === value);
      if (participante) {
        setEscriturador(participante);
        updateField({ name: 'escrituradorId', value: participante.id });
        updateField({ name: 'escriturador', value: participante.razaoSocial });
      } else {
        setEscriturador(null);
        updateField({ name: 'escrituradorId', value: '' });
        updateField({ name: 'escriturador', value });
      }
    } else {
      setEscriturador(value);
      updateField({ name: 'escrituradorId', value: value?.id ?? '' });
      updateField({ name: 'escriturador', value: value?.razaoSocial ?? '' });
    }
  }, [escrituradores, updateField]);

  const handleCustodianteChange = useCallback((value: ParticipanteResponse | string | null) => {
    if (typeof value === 'string') {
      const participante = custodiantes.find(p => p.razaoSocial === value);
      if (participante) {
        setCustodiante(participante);
        updateField({ name: 'custodianteId', value: participante.id });
        updateField({ name: 'custodiante', value: participante.razaoSocial });
      } else {
        setCustodiante(null);
        updateField({ name: 'custodianteId', value: '' });
        updateField({ name: 'custodiante', value });
      }
    } else {
      setCustodiante(value);
      updateField({ name: 'custodianteId', value: value?.id ?? '' });
      updateField({ name: 'custodiante', value: value?.razaoSocial ?? '' });
    }
  }, [custodiantes, updateField]);

  const handleAdministradorFiduciarioChange = useCallback((value: ParticipanteResponse | string | null) => {
    if (typeof value === 'string') {
      const participante = administradoresFiduciarios.find(p => p.razaoSocial === value);
      if (participante) {
        setAdministradorFiduciario(participante);
        updateField({ name: 'administradorFiduciarioId', value: participante.id });
        updateField({ name: 'administradorFiduciario', value: participante.razaoSocial });
      } else {
        setAdministradorFiduciario(null);
        updateField({ name: 'administradorFiduciarioId', value: '' });
        updateField({ name: 'administradorFiduciario', value });
      }
    } else {
      setAdministradorFiduciario(value);
      updateField({ name: 'administradorFiduciarioId', value: value?.id ?? '' });
      updateField({ name: 'administradorFiduciario', value: value?.razaoSocial ?? '' });
    }
  }, [administradoresFiduciarios, updateField]);

  const fetchFundInfo = useCallback(async (cnpj: string) => {
    if (validaCpfCnpj(cnpj)) {
      try {
        const data = await instrumentoApi.getConsultarFundoAnbimaPorCnpj(dispatch, cnpj);
        
        setAnbimaFundoData(data);
        if (data) {
          if (data.nome_comercial_fundo) {
            updateField({ name: 'nomeFantasia', value: data.nome_comercial_fundo });
          }
          
          if (data.codigo_fundo) {
            updateField({ name: 'codigoAtivo', value: data.codigo_fundo });
          }
          
          if (data.codigo_fundo) {
            updateField({ name: 'codigoANBIMA', value: data.codigo_fundo });
          }
          
          if (data.tipo_fundo) {
            const tipoFundoOption = customSelectOptions.find(option => option.value === data.tipo_fundo);
            if (tipoFundoOption) {
              updateField({ name: 'tipoFundoUI', value: tipoFundoOption });
              updateField({ name: 'tipoFundo', value: tipoFundoOption.value });
            }
          }
          
          if (data.razao_social_fundo) {
            updateField({ name: 'descricaoFundo', value: data.razao_social_fundo });
          }
          
          const administrador = data.prestadores_fundo?.find(p => p.codigo_tipo_prestador.toUpperCase() === 'ADMINISTRADOR');
          if (administrador) {
            handleAdministradorFiduciarioChange(administrador.razao_social);
          }
          
          const gestor = data.prestadores_fundo?.find(p => p.codigo_tipo_prestador.toUpperCase() === 'GESTOR');
          if (gestor) {
            handleGestorChange(gestor.razao_social);
          }

          const escriturador = data.prestadores_fundo?.find(p => p.codigo_tipo_prestador.toUpperCase() === 'ESCRITURADOR');
          if (escriturador) {
            handleEscrituradorChange(escriturador.razao_social);
          }
          
          const custodiante = data.prestadores_fundo?.find(p => p.codigo_tipo_prestador.toUpperCase() === 'CUSTODIANTE');
          if (custodiante) {
            handleCustodianteChange(custodiante.razao_social);
          }
          
          if (data.classes && data.classes.length > 0) {
            const classe = data.classes[0];
            if (classe.codigo_classe) {
              updateField({ name: 'classe', value: classe.codigo_classe });
            }
            if (classe.nivel1_categoria) {
              updateField({ name: 'classificacaoANBIMA', value: classe.nivel1_categoria });
            }
            if (classe.subclasses && classe.subclasses.length > 0) {
              const subclasse = classe.subclasses[0];

              if (subclasse.codigo_subclasse) {
                updateField({ name: 'serie', value: subclasse.codigo_subclasse });
              }

              if (subclasse.restricao_investimento) {
                updateField({ name: 'fundoExclusivo', value: subclasse.restricao_investimento });
              }

              if (subclasse.dias_carencia_inicial) {
                updateField({ 
                  name: 'carencia', 
                  value: parseInt(subclasse.dias_carencia_inicial) 
                });
              }
              
              if (subclasse.aplicacao_automatica) {
                const isAutomatica = subclasse.aplicacao_automatica.toUpperCase() === 'SIM';
                const option = options.find(o => (o.value === 'SIM') === isAutomatica) || options[0];
                updateField({ name: 'especificacaoQuantidadeAutomaticaUI', value: option });
                updateField({ name: 'especificacaoQuantidadeAutomatica', value: isAutomatica });
              }

              if (subclasse.prazo_emissao_cota) {
                updateField({ name: 'prazoCotacaoAplicacao', value: subclasse.prazo_emissao_cota });
              }
              
              if (subclasse.prazo_conversao_resgate) {
                updateField({ name: 'prazoCotacaoResgate', value: subclasse.prazo_conversao_resgate });
              }
              
              if (subclasse.prazo_pagamento_resgate) {
                updateField({ name: 'prazoLiquidacaoResgate', value: subclasse.prazo_pagamento_resgate });
              }
            }  
          }
        }
        
        return data;
      } catch (error) {
        console.error('Error fetching fund info from ANBIMA:', error);
        dispatch(addAlert({ message: 'Erro ao buscar informações do fundo na ANBIMA', severity: 'error' }));
        return null;
      }
    }
    return null;
  }, [dispatch, updateField, handleAdministradorFiduciarioChange, handleGestorChange, options, customSelectOptions, instrumentoApi]);

  const getNumeroEmissoes = useCallback(async (cnpj: string) => {
    if (validaCpfCnpj(cnpj)) {
      try {
        const result = await getNumeroEmissoesApi(
          dispatch,
          cnpj,
          InstrumentoFinanceiro.CotasDeFundo
        );
        const numero = parseInt(result) + 1;
        updateField({ name: 'numeroEmissao', value: numero.toString() });
        return numero.toString();
      } catch (error) {
      }
    }
    return null;
  }, [dispatch, getNumeroEmissoesApi, updateField]);

  useEffect(() => {
    setParticipantes();
    
    if (caracteristicas.fundoExclusivo !== undefined && !caracteristicas.fundoExclusivoUI) {
      updateField({
        name: 'fundoExclusivoUI',
        value: options.find(o => (o.value === 'SIM') === caracteristicas.fundoExclusivo) || options[0]
      });
    }
    
    if (caracteristicas.tipoFundo && !caracteristicas.tipoFundoUI) {
      updateField({
        name: 'tipoFundoUI',
        value: (tiposFundo || []).find(o => o.value === caracteristicas.tipoFundo) || (tiposFundo || [])[0]
      });
    }
    
    if (caracteristicas.especificacaoQuantidadeAutomatica !== undefined && !caracteristicas.especificacaoQuantidadeAutomaticaUI) {
      updateField({
        name: 'especificacaoQuantidadeAutomaticaUI',
        value: options.find(o => (o.value === 'SIM') === caracteristicas.especificacaoQuantidadeAutomatica) || options[0]
      });
    }
    
    if (caracteristicas.custoCotista && !caracteristicas.custoCotistaUI) {
      updateField({
        name: 'custoCotistaUI',
        value: options.find(o => o.value === caracteristicas.custoCotista) || options[0]
      });
    }
    
    if (caracteristicas.confirmacaoOperacao && !caracteristicas.confirmacaoOperacaoUI) {
      updateField({
        name: 'confirmacaoOperacaoUI',
        value: options.find(o => o.value === caracteristicas.confirmacaoOperacao) || options[0]
      });
    }
    
    if (caracteristicas.temPrevisaoPenalty !== undefined && !caracteristicas.temPrevisaoPenaltyUI) {
      updateField({
        name: 'temPrevisaoPenaltyUI',
        value: options.find(o => (o.value === 'SIM') === caracteristicas.temPrevisaoPenalty) || options[0]
      });
    }
    
    if (caracteristicas.dataAutorizacaoCVM && !caracteristicas.dataAutorizacaoCVMUI) {
      updateField({
        name: 'dataAutorizacaoCVMUI',
        value: caracteristicas.dataAutorizacaoCVM
      });
    }
  }, [setParticipantes, caracteristicas, options, tiposFundo, updateField]);

  useEffect(() => {
    if (!documents.length) {
      props.updateDocuments(
        documentOptions.map((option) => ({
          id: option.value,
          type: option.value,
          optional: false
        }))
      );
    }
  }, [documents]);

  useEffect(() => {
    if (documents.length) {
      const newestDocuments = [...documents]
        .sort((doc1, doc2) => {
          const date1 = doc1.remoteFileInfo?.creationDate ? new Date(doc1.remoteFileInfo.creationDate).getTime() : 0;
          const date2 = doc2.remoteFileInfo?.creationDate ? new Date(doc2.remoteFileInfo.creationDate).getTime() : 0;
          return date2 - date1;
        })
        .reduce((acc: DocumentInfo[], doc) => {
          if (!acc.some(d => d.type === doc.type)) {
            acc.push(doc);
          }
          return acc;
        }, []);

      const newUploadDocuments = documentOptions.map((option) => {
        const document = newestDocuments.find(doc => doc.type === option.value);
        return document ? { ...document, optional: false } : { type: option.value, optional: false };
      });
      props.updateDocuments(newUploadDocuments);

      const newDocumentsToDelete: DocumentInfo[] = [];
      documents.forEach((document) => {
        if (!documentOptions.some((option) => option.value === document.type)) {
          newDocumentsToDelete.push({ ...document });
        }
      });
      if (newDocumentsToDelete.length) {
        props.updateDocumentsToDelete([...(documentsToDelete || []), ...newDocumentsToDelete]);
      }
    }
  }, []);

  const handleUpdateFile = (attachments: Attachment[]) => {
    let updatedDocuments = [...documents];
    attachments.forEach(attachment => {
      const docMatchingTypeIndex = updatedDocuments.findIndex(doc => attachment.id === doc.type);
      if (docMatchingTypeIndex >= 0) {
        updatedDocuments = documents.map((doc, index) => {
          return index === docMatchingTypeIndex ? { ...doc, file: attachment.file } : doc;
        });
      }
      props.updateDocuments(updatedDocuments);
    });
  };

  const handleDeleteFileType = async (type: string) => {
    for (const document of documents) {
      if (document.type === type && !!document.remoteFileInfo) {
        await props.updateDocumentsToDelete((documentsToDelete || []).concat({ ...document }));
      }
    }

    let countOfType = documents.filter(document => document.type === type).length;
    const updatedDocs = documents
      .map((doc) => {
        if (doc.type === type) {
          return { ...doc, file: undefined, remoteFileInfo: undefined };
        }
        return doc;
      })
      .filter((documentInfo) => {
        if (documentInfo.type === type && countOfType !== 1) {
          countOfType--;
          return false;
        }
        return true;
      });
    props.updateDocuments(updatedDocs);
  };

  return (
    <div>
      <Section title="Informações Básicas do Fundo" defaultGrid>
        <TextField
          label={labels.cnpjFundo}
          value={isUsuarioEmissor ? CpfCnpjMask(usuarioLogado.participante.cnpj) : CpfCnpjMask(caracteristicas.cnpjFundo || '')}
          onChange={(value) => {
            if (!isUsuarioEmissor) {
              const sanitizedValue = sanitizeAlphanumericCnpj(value).substring(0, 14);
              updateField({ name: 'cnpjFundo', value: sanitizedValue });
              
              if (sanitizedValue.length === 14) {
                getNumeroEmissoes(sanitizedValue);
              } else if (sanitizedValue.length < 14 && caracteristicas.cnpjFundo?.length === 14) {
                dispatch(resetCotasDeFundoDetails());
                clearLocalFields();
                updateField({ name: 'cnpjFundo', value: sanitizedValue });
                dispatch(addAlert({ message: 'Campos limpos devido à alteração do CNPJ', severity: 'info' }));
              }
            }
          }}
          required
          disabled={isUsuarioEmissor}
          error={showError(labels.cnpjFundo)}
        />
        {!isUsuarioEmissor && REACT_APP_SHOW_ANBIMA_BUTTON && (
          <Button 
            variant="contained" 
            color="primary" 
            size="small"
            className={classes.searchButton}
            onClick={() => fetchFundInfo(caracteristicas.cnpjFundo || '')}
            disabled={sanitizeAlphanumericCnpj(caracteristicas.cnpjFundo || '').length !== 14}
          >
            Pesquisa ANBIMA
          </Button>
        )}
        <ParticipanteAutoComplete
          label={labels.administradorFiduciario}
          participantes={administradoresFiduciarios}
          participante={administradorFiduciario}
          onChange={handleAdministradorFiduciarioChange}
          error={showError(labels.administradorFiduciario)}
          disabled={[
            TipoRegistroInstrumentoFinanceiro.Aditamento,
            TipoRegistroInstrumentoFinanceiro.EdicaoEmitido
          ].includes(typeOfRegister) || isUsuarioAdministradorFiduciario}
        />
        <TextField
          label={labels.nomeFantasia}
          value={caracteristicas.nomeFantasia}
          onChange={(value) => updateField({ name: 'nomeFantasia', value })}
          required
          error={showError(labels.nomeFantasia)}
        />
        <TextField
          label={labels.gestor}
          value={gestor}
          onChange={(value) => handleGestorChange(value)}
          required
          error={showError(labels.gestor)}
          disabled={[
            TipoRegistroInstrumentoFinanceiro.Aditamento,
            TipoRegistroInstrumentoFinanceiro.EdicaoEmitido
          ].includes(typeOfRegister)}
        />
        <ParticipanteAutoComplete
          label={labels.escriturador}
          participantes={escrituradores}
          participante={escriturador}
          onChange={handleEscrituradorChange}
          error={showError(labels.escriturador)}
          disabled={[
            TipoRegistroInstrumentoFinanceiro.Aditamento,
            TipoRegistroInstrumentoFinanceiro.EdicaoEmitido
          ].includes(typeOfRegister)}
        />
        <ParticipanteAutoComplete
          label={labels.custodiante}
          participantes={custodiantes}
          participante={custodiante}
          onChange={handleCustodianteChange}
          error={showError(labels.custodiante)}
          disabled={[
            TipoRegistroInstrumentoFinanceiro.Aditamento,
            TipoRegistroInstrumentoFinanceiro.EdicaoEmitido
          ].includes(typeOfRegister)}
        />
        <TextField
          label={labels.codigoAtivo}
          value={caracteristicas.codigoAtivo}
          onChange={(value) => updateField({ name: 'codigoAtivo', value })}
          required
          error={showError(labels.codigoAtivo)}
        />
        <TextField
          label={labels.classe}
          value={caracteristicas.classe}
          onChange={(value) => updateField({ name: 'classe', value })}
          required
          error={showError(labels.classe)}
        />
        <TextField
          label={labels.serie}
          value={caracteristicas.serie}
          onChange={(value) => updateField({ name: 'serie', value })}
          required
          error={showError(labels.serie)}
        />
        <TextField
          label={labels.isin}
          value={caracteristicas.isin}
          onChange={(value) => updateField({ name: 'isin', value })}
        />
        <TextField
          label={labels.classificacaoANBIMA}
          value={caracteristicas.classificacaoANBIMA}
          onChange={(value) => updateField({ name: 'classificacaoANBIMA', value })}
          required
        />
        <TextField
          label={labels.codigoANBIMA}
          value={caracteristicas.codigoANBIMA}
          onChange={(value) => updateField({ name: 'codigoANBIMA', value })}
          required
        />
        <TextField
          label={labels.numeroAutorizacaoCVM}
          value={caracteristicas.numeroAutorizacaoCVM?.toString() || ''}
          onChange={(value) => {
            const numericValue = value.replace(/\D/g, '');
            updateField({ name: 'numeroAutorizacaoCVM', value: numericValue ? Number(numericValue) : undefined });
          }}
          error={showError(labels.numeroAutorizacaoCVM)}
        />
        <DatePicker
          label={labels.dataAutorizacaoCVM}
          value={caracteristicas.dataAutorizacaoCVM}
          onChange={(date) => {
            updateField({ name: 'dataAutorizacaoCVMUI', value: date });
            updateField({ name: 'dataAutorizacaoCVM', value: date });
          }}
          error={showError(labels.dataAutorizacaoCVM)}
        />
      </Section>

      <Section title="Características do Fundo" defaultGrid>
        <Dropdown
          label={labels.fundoExclusivo}
          required
          options={[
            { value: 'SIM', description: 'SIM' },
            { value: 'NÃO', description: 'NÃO' }
          ]}
          value={caracteristicas.fundoExclusivoUI?.value || ''}
          onChange={(value) => {
            const booleanOptions = [
              { value: 'SIM', description: 'SIM' },
              { value: 'NÃO', description: 'NÃO' }
            ];
            const option = booleanOptions.find((s) => s.value === value) ?? booleanOptions[1];
            updateField({ name: 'fundoExclusivoUI', value: option });
            updateField({ name: 'fundoExclusivo', value: option.value === 'SIM' });
          }}
          error={showError(labels.fundoExclusivo)}
        />
        <TextField
          label={`${labels.carencia} (dias)`}
          value={caracteristicas.carencia?.toString() || ''}
          onChange={(value) => {
            const numericValue = value.replace(/\D/g, '');
            updateField({ name: 'carencia', value: numericValue ? parseInt(numericValue, 10) : undefined });
          }}
          required
          error={showError(labels.carencia)}
        />
        <Dropdown
          label={labels.tipoFundo}
          required
          options={customSelectOptions}
          value={caracteristicas.tipoFundoUI?.value || ''}
          onChange={(value) => {
            const option = customSelectOptions.find((s) => s.value === value) ?? customSelectOptions[0];
            updateField({ name: 'tipoFundoUI', value: option });
            updateField({ name: 'tipoFundo', value: option.value });
          }}
          error={showError(labels.tipoFundo)}
        />
        <div className={classes.flexContainer}>
          <NumberField
            label={labels.casasDecimaisConversao}
            value={caracteristicas.casasDecimaisConversao}
            onChange={(value) => updateField({ name: 'casasDecimaisConversao', value })}
            numberType="integer"
            required
            error={showError(labels.casasDecimaisConversao)}
          />
        </div>
        <TextField
          label={labels.criterioEspecificacaoCotas}
          value={caracteristicas.criterioEspecificacaoCotas}
          onChange={(value) => updateField({ name: 'criterioEspecificacaoCotas', value })}
          required
          error={showError(labels.criterioEspecificacaoCotas)}
        />
        <Dropdown
          label={labels.especificacaoQuantidadeAutomatica}
          required
          options={[
            { value: 'SIM', description: 'SIM' },
            { value: 'NÃO', description: 'NÃO' }
          ]}
          value={caracteristicas.especificacaoQuantidadeAutomaticaUI?.value || ''}
          onChange={(value) => {
            const booleanOptions = [
              { value: 'SIM', description: 'SIM' },
              { value: 'NÃO', description: 'NÃO' }
            ];
            const option = booleanOptions.find((s) => s.value === value) ?? booleanOptions[1];
            updateField({ name: 'especificacaoQuantidadeAutomaticaUI', value: option });
            updateField({ name: 'especificacaoQuantidadeAutomatica', value: option.value === 'SIM' });
          }}
          error={showError(labels.especificacaoQuantidadeAutomatica)}
        />
        <Dropdown
          label={labels.custoCotista}
          options={[
            { value: 'COTISTA', description: 'COTISTA' },
            { value: 'OUTROS', description: 'OUTROS' }
          ]}
          value={caracteristicas.custoCotistaUI?.value || ''}
          onChange={(value) => {
            const custoCotistaOptions = [
              { value: 'COTISTA', description: 'COTISTA' },
              { value: 'OUTROS', description: 'OUTROS' }
            ];
            const option = custoCotistaOptions.find((s) => s.value === value) ?? custoCotistaOptions[0];
            updateField({ name: 'custoCotistaUI', value: option });
            updateField({ name: 'custoCotista', value: option.value });
          }}
          error={showError(labels.custoCotista)}
        />
        <Dropdown
          label={labels.confirmacaoOperacao}
          options={[
            { value: 'EMISSOR', description: 'EMISSOR' },
            { value: 'OUTROS', description: 'OUTROS' }
          ]}
          value={caracteristicas.confirmacaoOperacaoUI?.value || ''}
          onChange={(value) => {
            const confirmacaoOperacaoOptions = [
              { value: 'EMISSOR', description: 'EMISSOR' },
              { value: 'OUTROS', description: 'OUTROS' }
            ];
            const option = confirmacaoOperacaoOptions.find((s) => s.value === value) ?? confirmacaoOperacaoOptions[0];
            updateField({ name: 'confirmacaoOperacaoUI', value: option });
            updateField({ name: 'confirmacaoOperacao', value: option.value });
          }}
          error={showError(labels.confirmacaoOperacao)}
        />
      </Section>

      <Section title="Prazos e Condições" defaultGrid>
        <NumberField
          label={`${labels.prazoCotacaoAplicacao} (dias)`}
          value={caracteristicas.prazoCotacaoAplicacao}
          onChange={(value) => updateField({ name: 'prazoCotacaoAplicacao', value })}
          numberType="integer"
          required
          error={showError(labels.prazoCotacaoAplicacao)}
        />
        <NumberField
          label={`${labels.prazoCotacaoResgate} (dias)`}
          value={caracteristicas.prazoCotacaoResgate}
          onChange={(value) => updateField({ name: 'prazoCotacaoResgate', value })}
          numberType="integer"
          required
          error={showError(labels.prazoCotacaoResgate)}
        />
        <NumberField
          label={`${labels.prazoLiquidacaoResgate} (dias)`}
          value={caracteristicas.prazoLiquidacaoResgate}
          onChange={(value) => updateField({ name: 'prazoLiquidacaoResgate', value })}
          numberType="integer"
          required
          error={showError(labels.prazoLiquidacaoResgate)}
        />
        <TextField
          label={labels.responsavelClassificacao}
          value={caracteristicas.responsavelClassificacaoFundo}
          onChange={(value) => updateField({ name: 'responsavelClassificacaoFundo', value })}
          error={showError(labels.responsavelClassificacao)}
        />
        <Dropdown
          label={labels.temPrevisaoPenalty}
          options={[
            { value: 'SIM', description: 'SIM' },
            { value: 'NÃO', description: 'NÃO' }
          ]}
          value={caracteristicas.temPrevisaoPenaltyUI?.value || ''}
          onChange={(value) => {
            const booleanOptions = [
              { value: 'SIM', description: 'SIM' },
              { value: 'NÃO', description: 'NÃO' }
            ];
            const option = booleanOptions.find((s) => s.value === value) ?? booleanOptions[1];
            updateField({ name: 'temPrevisaoPenaltyUI', value: option });
            updateField({ name: 'temPrevisaoPenalty', value: option.value === 'SIM' });
          }}
          error={showError(labels.temPrevisaoPenalty)}
        />
        {caracteristicas.temPrevisaoPenalty && (
          <NumberField
            label={labels.percentualPenalty}
            value={caracteristicas.percentualPenalty}
            onChange={(value) => updateField({ name: 'percentualPenalty', value })}
            numberType="eightDecimals"
            error={showError(labels.percentualPenalty)}
            adornment="%"
            adornmentPosition="end"
          />
        )}
        <Grid item xs={12}>
          <TextField
            label={labels.descricaoFundo}
            value={caracteristicas.descricaoFundo}
            onChange={(value) => updateField({ name: 'descricaoFundo', value })}
            multiline
            rows={4}
          />
        </Grid>
      </Section>

      <Section title="Upload de Documentos">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Dropdown
              label={'Selecione o tipo do documento'}
              required
              options={documentOptions}
              value={uploadDocumentType ?? ''}
              onChange={setUploadDocumentType}
            />
          </Grid>
          <Grid item xs={12}>
            <UploadDocs
              inputAttachments={documents
                .filter((attachment) => !!attachment.remoteFileInfo)
                .map(({ type, remoteFileInfo }) => ({
                  id: type,
                  remoteFileInfo,
                }))}
              fileId={`${uploadDocumentType}`}
              onChange={handleUpdateFile}
              onDelete={handleDeleteFileType}
              multipleFiles={false}
            />
          </Grid>
          {showDocumentosError && (
            <Grid item xs={12}>
              <Typography color="error" variant="caption">
                É necessário anexar pelo menos um documento.
              </Typography>
            </Grid>
          )}
          <Grid item xs={12}>
            {documents?.filter((doc, index, self) => index === self.findIndex((t) => t.type === doc.type))
              .filter(doc => !(doc.optional && !doc.file && !doc.remoteFileInfo))
              .map((document) => (
                <React.Fragment key={document.type}>
                  <Grid container spacing={1}>
                    <Grid item>
                      <CheckCircleIcon color={document.file || document.remoteFileInfo ? 'primary' : 'disabled'} />
                    </Grid>
                    <Grid item xs>
                      <Typography variant="body2">{documentLongDescriptions[document.type]}</Typography>
                    </Grid>
                  </Grid>
                </React.Fragment>
              ))}
          </Grid>
        </Grid>
      </Section>
    </div>
  );
};

const ConnectedCaracteristicas = connect(mapStateToProps, mapDispatchToProps)(Caracteristicas);

interface CaracteristicasProps {
  participantes: Array<ParticipanteResponse>;
  codigo?: string;
  delimitacaoEfetuada?: boolean;
  instrumentoApi?: any;
  onRef?: (ref: { clearLocalFields: () => void }) => void;
}

const CaracteristicasWrapper: React.FC<CaracteristicasProps> = (props) => {
  return <ConnectedCaracteristicas {...props} />;
};

export default CaracteristicasWrapper;